import type {ReactElement} from 'react';
import {useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {EmailMakerBlockManager} from '@Components/email-maker-block-manager/email-maker-block-manager';
import {useSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {setSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {ElementsSubPanel} from '@Components/email-maker-block-manager/components/elements-block-panel/elements-block-panel.types';
import {assertNever} from '@Utils/assert-never.util';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {requestAiRedesignModalOpen} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box-reducer';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {PanelHeaderProps} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {BLOCK_CATEGORY_TILES} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import {isAiEditEnabled} from '@EmailMakerReact/lib/email-maker-ai-library';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {EMAIL_MAKER_BLOCKS} from '@EmailMakerReact/email-maker-react.constants';
import type {BlockSheetHeaderOptions, MountedBlockSheetProps} from './email-maker-block-sheet.types';
import styles from './email-maker-block-sheet.module.scss';

const PANEL_ID = 'email-maker-block-sheet-panel';

export function EmailMakerBlockSheet(): ReactElement | null {
  const {editor} = useContext(emailMakerContext);
  const modalData = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL]);

  if (!modalData?.isOpen || !editor) {
    return null;
  }

  return <MountedBlockSheet editor={editor} />;
}

function MountedBlockSheet({editor}: MountedBlockSheetProps): ReactElement {
  const dispatch = useAppDispatch();
  const selectedCategory = useSelectedBlockPanel();
  const [elementsSubPanel, setElementsSubPanel] = useState<ElementsSubPanel>(ElementsSubPanel.ELEMENTS);
  const bodyRef = useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(bodyRef);
  useDefaultPanelClose(PANEL_ID);

  useCloseSheetOnCanvasInteraction(editor, MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL, {closeOnAdd: true});

  // The sections panel manages its own internal scroll and header shadow, so the sheet opts out of both for it.
  const isSectionsPanel = selectedCategory === EMAIL_MAKER_BLOCKS.CATEGORY_SECTIONS;
  const showHeaderShadow = topShadow && !isSectionsPanel;

  useEffect(() => {
    setElementsSubPanel(ElementsSubPanel.ELEMENTS);
  }, [selectedCategory]);

  useLayoutEffect(() => {
    bodyRef.current?.scrollTo({top: 0});
  }, [selectedCategory, elementsSubPanel]);

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL));
  };

  const onAiRedesign = (): void => {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL));
    dispatch(requestAiRedesignModalOpen());
  };

  const onBack = (): void => {
    dispatch(setSelectedBlockPanel(null));
  };

  const onElementsSubPanelBack = (): void => {
    setElementsSubPanel(ElementsSubPanel.ELEMENTS);
  };

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL} panelId={PANEL_ID}>
      <Panel
        panelHeader={renderPanelHeader({selectedCategory, elementsSubPanel, showShadow: showHeaderShadow, onClose, onBack, onElementsSubPanelBack})}
        panelContent={
          <PanelContent ref={bodyRef} className={`${styles.body} ${isSectionsPanel ? '' : styles.scrollableBody}`}>
            <EmailMakerBlockManager
              gjsEditor={editor}
              getIsAiEditEnabled={(): boolean => isAiEditEnabled(editor)}
              hideMobileHeader
              onAiRedesign={onAiRedesign}
              elementsSubPanel={elementsSubPanel}
              onElementsSubPanelChange={setElementsSubPanel}
              mobileBreakpoint={ScreenBreakpoints.LARGE}
            />
          </PanelContent>
        }
      />
    </EmailMakerBottomSheet>
  );
}

/**
 * The sheet owns the single header; the block manager suppresses its own via `hideMobileHeader` and the controlled
 * Elements sub-panel. When an Elements sub-panel (Text/Image) is open, the header names that sub-tab and its back
 * button returns to the Elements list rather than the top-level category list.
 */
function renderPanelHeader({selectedCategory, elementsSubPanel, showShadow, onClose, onBack, onElementsSubPanelBack}: BlockSheetHeaderOptions): ReactElement<PanelHeaderProps> {
  const shadowClass = showShadow ? styles.headerShadow : '';
  const elementsSubPanelTitle = selectedCategory === EMAIL_MAKER_BLOCKS.CATEGORY_ELEMENTS ? getElementsSubPanelTitle(elementsSubPanel) : null;
  if (elementsSubPanelTitle !== null) {
    return <PanelHeader title={elementsSubPanelTitle} showBackButton onBackButton={onElementsSubPanelBack} onClose={onClose} className={shadowClass} />;
  }
  if (selectedCategory !== null) {
    return <PanelHeader title={resolvePanelTitle(selectedCategory)} showBackButton onBackButton={onBack} onClose={onClose} className={shadowClass} />;
  }
  return <PanelHeader title={window.i18next.t('pmwjs_email_maker_add_elements')} onClose={onClose} className={shadowClass} />;
}

function getElementsSubPanelTitle(subPanel: ElementsSubPanel): string | null {
  switch (subPanel) {
    case ElementsSubPanel.TEXT:
      return window.i18next.t('pmwjs_text');
    case ElementsSubPanel.IMAGE:
      return window.i18next.t('pmwjs_image');
    case ElementsSubPanel.ELEMENTS:
      return null;
    default:
      return assertNever(subPanel);
  }
}

function resolvePanelTitle(selectedCategory: string | null): string {
  const tile = BLOCK_CATEGORY_TILES.find((entry) => entry.id === selectedCategory);
  if (tile) {
    return window.i18next.t(tile.labelKey);
  }
  return window.i18next.t('pmwjs_email_maker_add_elements');
}
